import { useCallback, useEffect, useRef, useState } from "react";
import { applySort, DEFAULT_SORT, type SortDir, type SortState } from "@/lib/tableSort";

const PAGE_SIZE = 20;
const BLOCK_SIZE = 100;

export interface UseDrillThroughTableParams<TResponse extends Record<string, unknown>, TRow> {
  selectedMonth: string | null;
  searchKey: string;
  drillFilters: Record<string, unknown>;
  getRows: (response: TResponse) => TRow[] | undefined;
  getTotal: (response: TResponse) => number | undefined;
  fetchPage: (
    selectedMonth: string,
    filters: Record<string, unknown>,
    signal?: AbortSignal,
    pagination?: { limit: number; skip: number },
  ) => Promise<TResponse>;
  buildFetchFilters: (sort: SortState) => Record<string, unknown>;
  errorMessage: string;
}

export interface DrillThroughTableResult<TResponse, TRow> {
  rows: TRow[];
  totalRows: number;
  response: TResponse | null;
  loading: boolean;
  refetching: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  sort: SortState;
  onSort: (key: string, direction: SortDir | null) => void;
  reload: () => void;
  loadMore: () => void;
}

interface TableState<TResponse, TRow> {
  rows: TRow[];
  totalRows: number;
  response: TResponse | null;
  loading: boolean;
  refetching: boolean;
  loadingMore: boolean;
  error: string | null;
  skip: number;
}

export function useDrillThroughTable<TResponse extends Record<string, unknown>, TRow>({
  selectedMonth,
  searchKey,
  drillFilters,
  getRows,
  getTotal,
  fetchPage,
  buildFetchFilters,
  errorMessage,
}: UseDrillThroughTableParams<TResponse, TRow>): DrillThroughTableResult<TResponse, TRow> {
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const [state, setState] = useState<TableState<TResponse, TRow>>({
    rows: [],
    totalRows: 0,
    response: null,
    loading: true,
    refetching: false,
    loadingMore: false,
    error: null,
    skip: 0,
  });
  
  const hasLoadedRef = useRef(false);

  const onSort = useCallback((key: string, direction: SortDir | null) => {
    setSort(applySort(key, direction));
  }, []);

  const load = useCallback(() => {
    if (!selectedMonth) {
      hasLoadedRef.current = false;
      setState({
        rows: [],
        totalRows: 0,
        response: null,
        loading: false,
        refetching: false,
        loadingMore: false,
        error: "Select a month from the chart to view drill-through data.",
        skip: 0,
      });
      return undefined;
    }

    const controller = new AbortController();
    const isFirstLoad = !hasLoadedRef.current;
    setState((current) => ({
      ...current,
      loading: isFirstLoad,
      refetching: !isFirstLoad,
      loadingMore: false,
      error: null,
      skip: 0,
    }));

    fetchPage(selectedMonth, buildFetchFilters(sort), controller.signal, { limit: PAGE_SIZE, skip: 0 })
      .then((response) => {
        if (controller.signal.aborted) return;
        hasLoadedRef.current = true;
        setState({
          rows: getRows(response) ?? [],
          totalRows: getTotal(response) ?? 0,
          response,
          loading: false,
          refetching: false,
          loadingMore: false,
          error: null,
          skip: 0,
        });
      })
      .catch((caught) => {
        if (controller.signal.aborted) return;
        setState((current) => ({
          ...current,
          loading: false,
          refetching: false,
          loadingMore: false,
          error: caught instanceof Error ? caught.message : errorMessage,
        }));
      });

    return () => controller.abort();
  }, [selectedMonth, sort, getRows, getTotal, fetchPage, buildFetchFilters, errorMessage]);

  useEffect(load, [selectedMonth, searchKey, drillFilters, sort, load]);

  const hasMore = state.rows.length < state.totalRows;

  const loadMore = useCallback(async () => {
    if (!selectedMonth || state.loading || state.refetching || state.loadingMore || !hasMore) return;

    setState((current) => ({ ...current, loadingMore: true }));

    let skip = state.skip;
    const targetSkip = skip + BLOCK_SIZE;

    while (skip < targetSkip && hasMore) {
      const nextSkip = skip + PAGE_SIZE;
      let response: TResponse;
      try {
        response = await fetchPage(selectedMonth, buildFetchFilters(sort), undefined, {
          limit: PAGE_SIZE,
          skip: nextSkip,
        });
      } catch {
        break;
      }

      skip = nextSkip;

      setState((current) => ({
        ...current,
        skip: nextSkip,
        rows: [...current.rows, ...(getRows(response) ?? [])],
        totalRows: getTotal(response) ?? current.totalRows,
        response,
      }));
    }

    setState((current) => ({ ...current, loadingMore: false }));
  }, [
    selectedMonth,
    state.loading,
    state.refetching,
    state.loadingMore,
    state.skip,
    hasMore,
    getRows,
    getTotal,
    fetchPage,
    buildFetchFilters,
    sort,
  ]);

  // Auto-fill the first block (up to 100 rows) after initial fetch, matching the shared paged-table hook's UX.
  useEffect(() => {
    if (!state.loading && !state.refetching && !state.loadingMore && state.skip === 0 && hasMore) {
      loadMore();
    }
  }, [state.loading, state.refetching, state.loadingMore, state.skip, hasMore, loadMore]);

  return {
    rows: state.rows,
    totalRows: state.totalRows,
    response: state.response,
    loading: state.loading,
    refetching: state.refetching,
    loadingMore: state.loadingMore,
    error: state.error,
    hasMore,
    sort,
    onSort,
    reload: load,
    loadMore,
  };
}
